// lib/agui-client.ts
// Tool call made by assistant (AG-UI protocol format)
export interface ToolCall {
  id: string;
  type: "function";
  function: {
    name: string;
    arguments: string;
  };
}

// Message type that supports both regular messages and tool results
export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "tool";
  content: string;
  // For assistant messages that make tool calls
  toolCalls?: ToolCall[];
  // For tool result messages
  toolCallId?: string;
  toolName?: string;
}

interface ThreadResponse {
  thread_id: string;
  room_id: string;
  runs: Record<string, RunResponse>;
  created: string;
  metadata?: Record<string, unknown> | null;
}

interface RunResponse {
  run_id: string;
  room_id: string;
  thread_id: string;
  parent_run_id?: string | null;
  created: string;
  finished?: string | null;
  run_input?: RunAgentInput | null;
  events?: unknown[];
  metadata?: Record<string, unknown> | null;
}

interface AGUIClientConfig {
  baseUrl: string;
  roomId: string;
  getAccessToken?: () => string | null;
}

export class AGUIClient {
  private baseUrl: string;
  private roomId: string;
  private threadId: string | null = null;
  private currentRunId: string | null = null;
  private getAccessToken?: () => string | null;

  constructor(config: AGUIClientConfig) {
    this.baseUrl = config.baseUrl;
    this.roomId = config.roomId;
    this.getAccessToken = config.getAccessToken;
  }

  private buildHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };

    if (this.getAccessToken) {
      const token = this.getAccessToken();
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  private get baseEndpoint() {
    return `${this.baseUrl}/api/v1/rooms/${this.roomId}/agui`;
  }

  /**
   * Creates a new thread. The backend also creates an initial run.
   * Returns both thread_id and the initial run_id.
   */
  async createThread(signal?: AbortSignal): Promise<{ threadId: string; runId: string }> {
    const res = await fetch(this.baseEndpoint, {
      method: "POST",
      headers: this.buildHeaders(),
      body: JSON.stringify({}),
      signal,
    });
    if (!res.ok) throw new Error(`Failed to create thread: ${res.status}`);
    const data: ThreadResponse = await res.json();
    this.threadId = data.thread_id;

    // Backend creates initial run with thread - extract first run_id
    const runIds = Object.keys(data.runs);
    if (runIds.length === 0) {
      throw new Error("Thread created but no initial run returned");
    }
    const runId = runIds[0];
    this.currentRunId = runId;

    return { threadId: data.thread_id, runId };
  }

  /**
   * Creates a new run for an existing thread.
   * Only needed for subsequent messages after the first.
   */
  async createRun(threadId: string, signal?: AbortSignal): Promise<string> {
    const res = await fetch(`${this.baseEndpoint}/${threadId}`, {
      method: "POST",
      headers: this.buildHeaders(),
      body: JSON.stringify({}),
      signal,
    });
    if (!res.ok) throw new Error(`Failed to create run: ${res.status}`);
    const data: RunResponse = await res.json();
    this.currentRunId = data.run_id;
    return data.run_id;
  }

  /**
   * Fetches a run's metadata, including its saved input (null if the run was
   * never started) and its `finished` timestamp (null while it is running).
   */
  async getRun(threadId: string, runId: string, signal?: AbortSignal): Promise<RunResponse> {
    const res = await fetch(`${this.baseEndpoint}/${threadId}/${runId}`, {
      headers: this.buildHeaders(),
      signal,
    });
    if (!res.ok) throw new Error(`Failed to get run: ${res.status}`);
    return res.json();
  }

  /**
   * Reconnects to a run that was started earlier, e.g. before a page reload.
   * The backend keeps running the agent when the client disconnects and
   * saves every event, so this replays the events after `afterIndex` (all of
   * them by default) and keeps streaming until the run finishes.
   */
  resumeRun(
    threadId: string,
    runId: string,
    afterIndex = -1,
    signal?: AbortSignal
  ): AsyncGenerator<AGUIEvent> {
    this.threadId = threadId;
    this.currentRunId = runId;
    return this.streamRun(threadId, runId, {}, signal, {
      "Last-Event-ID": `${runId}:${afterIndex}`,
    });
  }

  async *streamRun(
    threadId: string,
    runId: string,
    input: RunAgentInput | Record<string, never>,
    signal?: AbortSignal,
    extraHeaders: Record<string, string> = {}
  ): AsyncGenerator<AGUIEvent> {
    const res = await fetch(`${this.baseEndpoint}/${threadId}/${runId}`, {
      method: "POST",
      headers: { ...this.buildHeaders(), ...extraHeaders },
      body: JSON.stringify(input),
      signal,
    });

    if (!res.ok) throw new Error(`Failed to stream run: ${res.status}`);
    if (!res.body) throw new Error("No response body");

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    let eventCount = 0;
    let chunkCount = 0;

    console.log(`[AGUI SSE] Stream started for ${threadId}/${runId}`);

    while (true) {
      const { done, value } = await reader.read();
      chunkCount++;
      if (done) {
        // Process any remaining data in the buffer
        buffer += decoder.decode();
        console.log(`[AGUI SSE] Stream ended after ${chunkCount} chunks, ${eventCount} events. Remaining buffer: "${buffer.slice(0, 200)}"`);
      } else {
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;
        console.log(`[AGUI SSE] Chunk #${chunkCount} (${chunk.length} bytes)`);
      }

      const lines = buffer.split("\n");
      buffer = done ? "" : (lines.pop() || "");

      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.startsWith("data: ")) {
          const data = trimmed.slice(6);
          if (data === "[DONE]") {
            console.log(`[AGUI SSE] Received [DONE] after ${eventCount} events`);
            return;
          }
          try {
            const parsed = JSON.parse(data) as AGUIEvent;
            eventCount++;
            // Log all non-content events (content events are too noisy)
            if (parsed.type !== "TEXT_MESSAGE_CONTENT" && parsed.type !== "TOOL_CALL_ARGS" && parsed.type !== "THINKING_TEXT_MESSAGE_CONTENT") {
              console.log(`[AGUI SSE] Event #${eventCount}:`, parsed.type, JSON.stringify(parsed).slice(0, 200));
            }
            yield parsed;
          } catch {
            console.warn(`[AGUI SSE] Malformed JSON:`, data.slice(0, 200));
          }
        } else if (trimmed.length > 0 && !trimmed.startsWith(":")) {
          // Log non-empty, non-comment lines that aren't data lines
          console.log(`[AGUI SSE] Non-data line: "${trimmed.slice(0, 100)}"`);
        }
      }

      if (done) break;
    }
    console.log(`[AGUI SSE] Loop exited. Total: ${eventCount} events from ${chunkCount} chunks`);
  }

  async chat(
    messages: ChatMessage[],
    tools: ToolDefinition[] = [],
    signal?: AbortSignal
  ): Promise<AsyncGenerator<AGUIEvent>> {
    let threadId: string;
    let runId: string;

    if (!this.threadId) {
      // First message: create thread (which includes initial run)
      const result = await this.createThread(signal);
      threadId = result.threadId;
      runId = result.runId;
    } else {
      // Subsequent messages: create new run for existing thread
      threadId = this.threadId;
      try {
        runId = await this.createRun(threadId, signal);
      } catch (err) {
        if (signal?.aborted) throw err;
        // A restored thread may no longer exist on the server (e.g. it was
        // evicted after a page reload). Fall back to a fresh thread instead
        // of failing the whole conversation.
        console.warn(
          "[AGUI] Could not resume thread, starting a new one:",
          err
        );
        this.threadId = null;
        const result = await this.createThread(signal);
        threadId = result.threadId;
        runId = result.runId;
      }
    }

    // Build the AG-UI RunAgentInput with required thread_id and run_id
    // Assistant messages with tool calls need toolCalls array
    // Tool result messages need toolCallId field
    // Format messages for the backend using snake_case field names
    // (OpenAI expects tool_calls / tool_call_id, not camelCase)
    const formattedMessages = messages.map((m, idx) => {
      const base: Record<string, unknown> = {
        id: `msg_${idx}`,
        role: m.role,
        content: m.content,
      };
      // Assistant messages may have tool calls
      if (m.role === "assistant" && m.toolCalls && m.toolCalls.length > 0) {
        base.tool_calls = m.toolCalls;
      }
      // Tool result messages must include tool_call_id
      if (m.role === "tool" && m.toolCallId) {
        base.tool_call_id = m.toolCallId;
      }
      return base;
    });

    // Debug: log what we're sending
    console.log("[AGUI] Sending messages:", JSON.stringify(formattedMessages, null, 2));

    const input: RunAgentInput = {
      thread_id: threadId,
      run_id: runId,
      messages: formattedMessages,
      tools: tools.map(t => ({
        name: t.name,
        description: t.description,
        parameters: t.parameters,
      })),
      context: [],
      state: null,
      forwarded_props: null,
    };

    return this.streamRun(threadId, runId, input, signal);
  }

  getThreadId(): string | null {
    return this.threadId;
  }

  getCurrentRunId(): string | null {
    return this.currentRunId;
  }

  setThreadId(id: string) {
    this.threadId = id;
  }
}

// AG-UI Protocol Types

// Message format for AG-UI protocol
export interface AGUIMessage {
  id: string;
  role: string;
  content: string;
  // For assistant messages that make tool calls
  toolCalls?: ToolCall[];
  // For tool result messages (required when role="tool")
  toolCallId?: string;
}

export interface RunAgentInput {
  thread_id: string;
  run_id: string;
  parent_run_id?: string | null;
  messages: Record<string, unknown>[];
  tools?: ToolDef[];
  context?: ContextItem[];
  state?: Record<string, unknown> | null;
  forwarded_props?: Record<string, unknown> | null;
}

export interface ToolDef {
  name: string;
  description: string;
  parameters: Record<string, unknown>;
}

export interface ContextItem {
  type: string;
  content: string;
}

export interface ToolDefinition {
  name: string;
  description: string;
  parameters: Record<string, unknown>;
  handler: (args: Record<string, unknown>) => Promise<unknown>;
}

// AG-UI Event types - backend uses UPPERCASE types
export type AGUIEvent =
  | { type: "TEXT_MESSAGE_START"; messageId: string }
  | { type: "TEXT_MESSAGE_CONTENT"; messageId: string; delta: string }
  | { type: "TEXT_MESSAGE_END"; messageId: string }
  | { type: "TOOL_CALL_START"; toolCallId: string; toolCallName: string }
  | { type: "TOOL_CALL_ARGS"; toolCallId: string; delta: string }
  | { type: "TOOL_CALL_END"; toolCallId: string }
  | { type: "RUN_STARTED"; runId: string; threadId: string }
  | { type: "RUN_FINISHED"; runId: string; threadId: string }
  | { type: "RUN_ERROR"; message: string }
  | { type: "THINKING_START" }
  | { type: "THINKING_TEXT_MESSAGE_CONTENT"; delta: string }
  | { type: "THINKING_END" }
  | { type: string; [key: string]: unknown }; // Catch-all for other events